import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import NewsCard from "@/components/base/NewsCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { newsList, newsCategories } from "@/mocks/news";

const PAGE_SIZE = 6;

type SortKey = "newest" | "oldest" | "hot";

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "newest", label: "最新发布" },
  { key: "oldest", label: "最早发布" },
  { key: "hot", label: "最热阅读" },
];

export default function NewsListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tagParam = searchParams.get("tag") ?? "全部";
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortKey>("newest");
  const [panelOpen, setPanelOpen] = useState(false);

  const tags = ["全部", ...newsCategories];

  const filtered = useMemo(() => {
    const byTag =
      tagParam === "全部"
        ? [...newsList]
        : newsList.filter((item) => item.category === tagParam);

    if (sort === "newest") byTag.sort((a, b) => b.date.localeCompare(a.date));
    if (sort === "oldest") byTag.sort((a, b) => a.date.localeCompare(b.date));
    if (sort === "hot") byTag.sort((a, b) => b.views - a.views);

    return byTag;
  }, [tagParam, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const changeTag = (tag: string) => {
    setPage(1);
    if (tag === "全部") setSearchParams({});
    else setSearchParams({ tag });
  };

  const resetFilters = () => {
    setPage(1);
    setSort("newest");
    setSearchParams({});
    setPanelOpen(false);
  };

  return (
    <SiteLayout
      title="白石茉莉奈资讯列表 - 明星动态平台"
      description="提供白石茉莉奈最新新闻和活动报道，涵盖采访专访、作品发布、荣誉奖项与幕后花絮，按标签与热度快速筛选。"
      keywords="白石茉莉奈资讯,白石茉莉奈新闻,活动报道,采访专访,明星动态"
    >
      <div className="px-4 md:px-8 pt-6 md:pt-8">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "资讯" }]} />
        <div className="mt-5 md:mt-7">
          <span className="text-xs font-body tracking-[0.3em] text-accent-600">
            NEWS & UPDATES
          </span>
          <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 mt-2 tracking-wide">
            白石茉莉奈最新动态与新闻
          </h1>
          <p className="text-sm md:text-base text-foreground-500 mt-3 max-w-2xl font-body">
            汇集活动报道、采访专访、作品发布等各类资讯，按标签筛选与排序，快速找到你关注的内容。
          </p>
        </div>
      </div>

      <div className="px-4 md:px-8 py-8 md:py-10">
        <div className="hidden md:flex items-center justify-between gap-6 mb-8 pb-5 border-b border-background-200">
          <div className="flex flex-wrap items-center gap-2">
            {tags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => changeTag(tag)}
                className={`text-sm font-body px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                  tag === tagParam
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-sm text-foreground-400 font-body">排序</span>
            <select
              value={sort}
              onChange={(e) => {
                setSort(e.target.value as SortKey);
                setPage(1);
              }}
              className="text-sm font-body bg-background-100 border border-background-200 rounded-md px-3 py-2 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
            >
              {sortOptions.map((opt) => (
                <option key={opt.key} value={opt.key}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="md:hidden flex items-center justify-between gap-3 mb-5">
          <button
            type="button"
            onClick={() => setPanelOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-background-300 text-sm font-body text-foreground-700 cursor-pointer"
          >
            <i className="ri-filter-3-line"></i>
            筛选 / 排序
          </button>
          <span className="text-sm text-foreground-400 font-body">
            共 {filtered.length} 条
          </span>
        </div>

        <p className="hidden md:block text-sm text-foreground-400 font-body mb-5">
          共 {filtered.length} 条资讯
        </p>

        {pageItems.length === 0 ? (
          <EmptyState
            title="暂时没有匹配的资讯"
            description="换个标签看看，或清空筛选查看全部动态。"
            actionLabel="清空筛选"
            onAction={resetFilters}
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 md:gap-5">
            {pageItems.map((item) => (
              <NewsCard key={item.id} item={item} />
            ))}
          </div>
        )}

        <Pagination current={safePage} total={totalPages} onChange={setPage} />
      </div>

      {panelOpen && (
        <div className="md:hidden fixed inset-0 z-[60] flex items-end">
          <div
            className="absolute inset-0 bg-foreground-950/60"
            onClick={() => setPanelOpen(false)}
          ></div>
          <div className="relative w-full bg-background-50 rounded-t-2xl p-5 pb-8 max-h-[80vh] overflow-y-auto animate-fade-up">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-lg text-foreground-950">
                筛选与排序
              </h3>
              <button
                type="button"
                onClick={() => setPanelOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-background-100 cursor-pointer"
                aria-label="关闭"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <p className="text-sm font-body text-foreground-500 mb-3">标签</p>
            <div className="flex flex-wrap gap-2 mb-6">
              {tags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => changeTag(tag)}
                  className={`text-sm font-body px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                    tag === tagParam
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600"
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>

            <p className="text-sm font-body text-foreground-500 mb-3">排序</p>
            <div className="flex flex-wrap gap-2 mb-6">
              {sortOptions.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => {
                    setSort(opt.key);
                    setPage(1);
                  }}
                  className={`text-sm font-body px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                    sort === opt.key
                      ? "bg-secondary-500 text-background-50"
                      : "bg-background-100 text-foreground-600"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={resetFilters}
                className="flex-1 py-3 rounded-md border border-background-300 text-sm font-body text-foreground-700 cursor-pointer"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setPanelOpen(false)}
                className="flex-1 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-body cursor-pointer"
              >
                查看结果
              </button>
            </div>
          </div>
        </div>
      )}
    </SiteLayout>
  );
}